import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "How much do you charge?",
    a: "Every property is different, so I quote per-job rather than a one-size-fits-all rate. Most end-of-tenancy deep cleans start from £195 for a studio/1-bed and scale up with size and condition. Full pricing is published on the site under 'Pricing' — send a quick request via the form and you'll get an honest, fixed quote within 24 hours, no hidden extras.",
  },
  {
    q: "Do you cover my area?",
    a: "Based in Leamington Spa, I cover Warwick, Kenilworth, Coventry, Stratford-upon-Avon, Southam, Rugby, Solihull and most of Warwickshire. Outside that? Just ask — bigger jobs make a bit of travel worthwhile.",
  },
  {
    q: "Are you insured?",
    a: "Yes. Full public liability cover up to £1m, and I'm DBS checked. I'm also a Checkatrade Approved tradesperson — vetted and listed on Checkatrade.com — so you (or your tenants) can be confident I'm safe to be in the property. Certificates available on request.",
  },
  {
    q: "Will I get my deposit back if I book a deep clean?",
    a: "I follow letting-agent end-of-tenancy checklists to the letter — oven, carpets, grout, skirtings, blinds, windows inside, the lot. While I can't guarantee what your landlord decides, I'd put my own deposit on the line for the standard I leave a property in.",
  },
  {
    q: "How quickly can you turn a property around?",
    a: "Most deep cleans and standard turnovers are completed inside 24 hours. Bigger jobs — repaints, repairs, garden tidies — usually run 2–3 days end-to-end. Urgent same-day requests? Call directly and I'll do what I can.",
  },
  {
    q: "Do I need to be there?",
    a: "Not at all. I'll work around keys left with you, your agent, or a key safe. Many landlords just leave me to it and check the result against photos sent at the end.",
  },
  {
    q: "What's included in a deep clean?",
    a: "Kitchen (inside oven, hob, extractor, fridge, cupboards), bathroom (full descale, grout, sealant check), all floors (vacuum + mop, carpet shampoo if booked), windows internal, skirtings, doors and frames, switches and sockets, blinds, and a final detail walk-through. Anything extra is priced up front, never sprung on you after.",
  },
  {
    q: "Can you do handyman repairs at the same time as a clean?",
    a: "That's the whole point. One trusted pair of hands means a single visit fixes the broken handle, repaints the scuffed wall AND deep-cleans the property — without juggling three trades. Bundle services to keep costs down.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" className="py-24 sm:py-32 border-t border-[#0B1215]/10 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="font-mono-label text-[#FF5A36] mb-4">· FREQUENTLY ASKED</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0B1215] tracking-tighter leading-[0.95]">
              Straight<br />
              <span className="italic font-light">answers</span>.
            </h2>
            <p className="mt-6 text-[#495057] leading-relaxed max-w-sm">
              The questions I get asked the most — answered properly. If yours isn't here, just
              drop me a message via the quote form below.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8 border-2 border-[#0B1215] bg-[#F8F9FA]"
            data-testid="faq-list"
          >
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="border-b border-[#0B1215]/10 last:border-b-0"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="px-6 py-5 font-display text-lg sm:text-xl tracking-tight text-left hover:no-underline hover:bg-[#E9ECEF] data-[state=open]:bg-[#0B1215] data-[state=open]:text-[#F8F9FA] transition-colors rounded-none">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-2 text-[#495057] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
